import { ColorType } from '@/theme/colors';
import { RadiusSize } from '@/theme/radius';
import { type BorderGradient, GradientPreset } from '@/theme/gradients';
import { ContainerPreset } from '@/constants/componentPresets';

const DARK_INSET_BOX_SHADOW =
  'inset rgb(0 0 0 / 35%) 2px 1px 15px 0px, #ffffff14 1px 1px 1px 0px, #ffffff09 -2px -2px 0px -1px';
const DARK_INSET_HOVER_BOX_SHADOW =
  `${DARK_INSET_BOX_SHADOW}, inset #66fec154 0px 0px 0px 1px`;

// "Mysterious" — the chatteaser3 card surface: a dark, frosted glass with mint
// radial corner-glows, a bright glass bevel, and a deep floating-panel shadow.
const MYSTERIOUS_CORNER_GLOW = [
  'radial-gradient(circle at 108% 102%, rgba(95,243,184,0.18) 0%, rgba(95,243,184,0.06) 30%, rgba(95,243,184,0.01) 60%, rgba(0,0,0,0) 80%)',
  'radial-gradient(circle at 4% 3%, rgba(95,243,184,0.16) 0%, rgba(95,243,184,0.05) 34%, rgba(95,243,184,0.01) 68%, rgba(0,0,0,0) 90%)',
].join(', ');
const MYSTERIOUS_BOX_SHADOW =
  'inset -2px -3px 5px -2px rgba(255,255,255,0.14), inset 1px 1px 1px 0 rgba(255,255,255,0.25), 0 50px 130px -40px rgba(0,0,0,0.9), 0 30px 60px -50px rgba(95,243,184,0.25)';

export interface PresetConfig {
  radius: RadiusSize | number;
  borderColor?: ColorType;
  borderGradient?: BorderGradient | GradientPreset;
  borderOpacity?: number;
  borderWidth: number;
  backgroundColor?: ColorType;
  backgroundGradient?: BorderGradient | GradientPreset;
  /** Raw CSS background-image, layered over backgroundColor — e.g. radial glows a linear gradient can't express. */
  backgroundImage?: string;
  backgroundOpacity?: number;
  backdropBlur?: number;
  padding: number;
  glow?: number;
  boxShadow?: string;
  hoverBoxShadow?: string;
  width?: string;
  height?: string;
  flexDirection?: 'row' | 'column';
  overflow?: 'visible' | 'hidden' | 'auto';
}

export const CONTAINER_PRESET_CONFIGS: Record<ContainerPreset, PresetConfig> = {
  [ContainerPreset.Default]: {
    radius: RadiusSize.Small,
    borderColor: ColorType.Transparent,
    borderOpacity: 0,
    borderWidth: 0,
    backgroundColor: ColorType.Transparent,
    backgroundOpacity: 0,
    backdropBlur: 0,
    padding: 16,
  },
  [ContainerPreset.Active]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.Accent,
    borderOpacity: 0.6,
    borderWidth: 1,
    backgroundColor: ColorType.Accent,
    backgroundOpacity: 0.15,
    padding: 10,
  },
  [ContainerPreset.Hollow]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.White,
    borderOpacity: 0.1,
    borderWidth: 1,
    backgroundColor: ColorType.Transparent,
    backgroundOpacity: 0,
    padding: 10,
    boxShadow:
      'rgba(0, 0, 0, 0.21) 2px 1px 2px 0px, rgba(255, 255, 255, 0.05) 1px 1px 10px 1px inset, rgba(255, 255, 255, 0.03) -1px -2px 0px 0px inset, rgba(0, 0, 0, 0.11) -1px -1px 2px 0px inset, rgba(255, 255, 255, 0.05) 0px 0px 0px 1px inset, rgba(255, 255, 255, 0.12) 0.7px 0.9px 1.1px 0px inset, rgba(255, 255, 255, 0.06) -0.5px -0.4px 0.5px 0px inset',
  },
  [ContainerPreset.Card]: {
    radius: RadiusSize.Medium,
    borderColor: ColorType.Accent,
    borderOpacity: 0.15,
    borderWidth: 1,
    backgroundColor: ColorType.Black,
    backgroundOpacity: 0.4,
    padding: 24,
  },
  [ContainerPreset.Secondary]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.White,
    borderOpacity: 0.08,
    borderWidth: 1,
    backgroundColor: ColorType.White,
    backgroundOpacity: 0.08,
    backdropBlur: 1,
    padding: 18,
    boxShadow:
      '#00000014 1px 3px 3px 0px,rgba(255, 255, 255, 0.06) 1px 1px 2px 1px inset, #0000002b 2px 3px 14px 0px inset, rgb(255 255 255 / 3%) -1px -2px 0px 0px inset, rgb(0 0 0 / 11%) -1px -1px 2px 0px inset',
  },
  [ContainerPreset.Elevated]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.Accent,
    borderOpacity: 0.25,
    borderWidth: 1,
    backgroundColor: ColorType.Black,
    backgroundOpacity: 0.5,
    padding: 32,
  },
  [ContainerPreset.Modern1]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.Transparent,
    borderOpacity: 0,
    borderWidth: 0,
    backgroundGradient: {
      angle: 160,
      colors: [
        { color: ColorType.Accent, opacity: 0.12, stop: 0 },
        { color: '#000E2A', opacity: 0.6, stop: 55 },
        { color: ColorType.Black, opacity: 0.7, stop: 100 },
      ],
    },
    backdropBlur: 8,
    padding: 24,
    boxShadow:
      'inset 1px 1px 2px 0px rgb(102 254 193 / 23%), inset -1px -1px 1px 0px rgb(102 254 193 / 17%), 0 30px 70px -25px rgba(0, 0, 0, .85)',
  },
  [ContainerPreset.Tile]: {
    radius: RadiusSize.Large,
    borderWidth: 1,
    backgroundGradient: {
      angle: 160,
      colors: [
        { color: '#002E1B', opacity: 0.14, stop: 0 },
        { color: '#000E2A', opacity: 0.66, stop: 55 },
        { color: ColorType.Black, opacity: 0.8, stop: 100 },
      ],
    },
    backdropBlur: 5,
    padding: 24,
    boxShadow:
      'rgba(0, 0, 0, 0.21) 2px 1px 15px 0px, rgba(255, 255, 255, 0.05) 1px 1px 10px 1px inset, rgba(255, 255, 255, 0.03) -1px -2px 0px 0px inset, rgba(0, 0, 0, 0.11) -1px -1px 2px 0px inset, rgba(255, 255, 255, 0.05) 0px 0px 0px 1px inset',
  },
  [ContainerPreset.TileGradient]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.Transparent,
    borderOpacity: 0,
    borderWidth: 0,
    backgroundGradient: {
      angle: 194,
      colors: [
        { color: ColorType.Accent, opacity: 0.08, stop: 0 },
        { color: ColorType.Accent, opacity: 0.06, stop: 20 },
        { color: ColorType.Accent, opacity: 0, stop: 100 },
      ],
    },
    backdropBlur: 5,
    padding: 24,
    boxShadow: 'rgba(102, 254, 193, 0.08) 0px 0px 0px 1px inset, rgba(0, 0, 0, 0.18) 0px 1px 6px 1px',
  },
  [ContainerPreset.ModalCard]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.White,
    borderOpacity: 0.05,
    borderWidth: 1,
    backgroundColor: ColorType.White,
    backgroundOpacity: 0.06,
    backdropBlur: 3,
    padding: 18,
    boxShadow:
      '#00000003 1px 3px 5px 0px,rgba(255, 255, 255, 0.06) 1px 1px 2px 1px inset, #0000002b 2px 3px 14px 0px inset, rgb(255 255 255 / 3%) -1px -2px 0px 0px inset, rgb(0 0 0 / 11%) -1px -1px 2px 0px inset',
    width: '100%',
    height: 'fit-content',
    flexDirection: 'column',
  },
  [ContainerPreset.HalfGlass]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.White,
    borderOpacity: 0.05,
    borderWidth: 1,
    backgroundGradient: {
      angle: -9,
      colors: [
        { color: ColorType.White, opacity: 0.06, stop: 30 },
        { color: ColorType.Transparent, opacity: 0, stop: 100 },
      ],
    },
    backdropBlur: 3,
    padding: 18,
    boxShadow:
      '#00000003 1px 3px 5px 0px,rgba(255, 255, 255, 0.06) 1px 1px 2px 1px inset, #0000002b 2px 3px 14px 0px inset, rgb(255 255 255 / 3%) -1px -2px 0px 0px inset, rgb(0 0 0 / 11%) -1px -1px 2px 0px inset',
    width: '100%',
    height: 'fit-content',
    flexDirection: 'column',
  },
  [ContainerPreset.HalfGlass2]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.White,
    borderOpacity: 0.08,
    borderWidth: 1,
    backgroundGradient: GradientPreset.GlassyCard,
    backdropBlur: 0,
    padding: 18,
    boxShadow:
      '#00000014 1px 3px 3px 0px, rgba(255, 255, 255, 0.06) 1px 1px 2px 1px inset, #0000002b 2px 3px 14px 0px inset, rgb(255 255 255 / 3%) -1px -2px 0px 0px inset, rgb(0 0 0 / 11%) -1px -1px 2px 0px inset',
    width: '100%',
    height: 'fit-content',
    flexDirection: 'column',
  },
  [ContainerPreset.ModernFaded]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.White,
    borderOpacity: 0.05,
    borderWidth: 1,
    backgroundGradient: {
      angle: 160,
      colors: [
        { color: ColorType.Accent, opacity: 0.1, stop: 0 },
        { color: '#000E2A', opacity: 0.3, stop: 55 },
        { color: ColorType.Black, opacity: 0.1, stop: 100 },
      ],
    },
    backdropBlur: 3,
    padding: 18,
    boxShadow:
      '#00000003 1px 3px 5px 0px,rgba(255, 255, 255, 0.06) 1px 1px 2px 1px inset, #0000002b 2px 3px 14px 0px inset, rgb(255 255 255 / 3%) -1px -2px 0px 0px inset, rgb(0 0 0 / 11%) -1px -1px 2px 0px inset',
    width: '100%',
    height: 'fit-content',
    flexDirection: 'column',
  },
  [ContainerPreset.TextInput]: {
    radius: RadiusSize.Small,
    borderColor: ColorType.Accent,
    borderOpacity: 0.15,
    borderWidth: 1,
    backgroundColor: ColorType.Black,
    backgroundOpacity: 0.25,
    backdropBlur: 10,
    padding: 8,
    width: '100%',
    height: 'fit-content',
  },
  [ContainerPreset.ChipVersion]: {
    radius: RadiusSize.Medium,
    borderColor: ColorType.Accent,
    borderOpacity: 0.2,
    borderWidth: 1,
    backgroundColor: ColorType.Accent,
    backgroundOpacity: 0.28,
    padding: 3,
  },
  [ContainerPreset.ChipDent]: {
    radius: RadiusSize.Medium,
    backgroundColor: ColorType.White,
    backgroundOpacity: 0.07,
    borderWidth: 0,
    padding: 5,
    boxShadow: '#00000033 0px 1px 1px 1px inset',
  },
  [ContainerPreset.InnerWindow]: {
    radius: RadiusSize.Rounded,
    borderColor: ColorType.Accent,
    borderOpacity: 0.17,
    borderWidth: 1,
    backgroundColor: ColorType.White,
    backgroundOpacity: 0.08,
    padding: 8,
    boxShadow: 'rgba(0, 0, 0, 0.48) 0px 1px 8px inset',
  },
  [ContainerPreset.WitnessCard]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.White,
    borderOpacity: 0.08,
    borderWidth: 1,
    backgroundColor: ColorType.Black,
    backgroundOpacity: 0.88,
    backdropBlur: 0,
    padding: 0,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    overflow: 'hidden',
  },
  [ContainerPreset.Blackish]: {
    radius: RadiusSize.Large,
    borderWidth: 1,
    backgroundColor: ColorType.Black,
    backgroundOpacity: 0.42,
    backdropBlur: 6,
    padding: 24,
    boxShadow:
      'rgba(0, 0, 0, 0.21) 2px 1px 15px 0px, rgba(255, 255, 255, 0.05) 1px 1px 10px 1px inset, rgba(255, 255, 255, 0.03) -1px -2px 0px 0px inset, rgba(0, 0, 0, 0.11) -1px -1px 2px 0px inset, rgba(255, 255, 255, 0.05) 0px 0px 0px 1px inset',
  },
  [ContainerPreset.DarkInset]: {
    radius: RadiusSize.Large,
    borderColor: ColorType.Transparent,
    borderOpacity: 0,
    borderWidth: 0,
    backgroundColor: ColorType.Black,
    backgroundOpacity: 0.32,
    padding: 18,
    boxShadow: DARK_INSET_BOX_SHADOW,
    hoverBoxShadow: DARK_INSET_HOVER_BOX_SHADOW,
  },
  [ContainerPreset.Mysterious]: {
    radius: 52,
    borderColor: ColorType.White,
    borderOpacity: 0.05,
    borderWidth: 1,
    backgroundColor: ColorType.Black,
    backgroundOpacity: 0.86,
    backgroundImage: MYSTERIOUS_CORNER_GLOW,
    padding: 38,
    overflow: 'hidden',
    boxShadow: MYSTERIOUS_BOX_SHADOW,
  },
};
